import React, { useState, useEffect, MouseEvent, RefObject } from 'react';
import useTranslations from '@hooks/useTranslations';
import { StockModelCardOptionsProps } from '.';
import { IconTemplate } from '../../../Icon/IconTemplate';
import { WithCollapsible, TWithCollapsible } from '../../../hoc/withCollapsible';
import { Icons } from '../../../Icon';
import { IconContainer, InformationTooltip, TooltipWrapper } from './StockModelCardOptionsStyled';
import { Trans } from 'react-i18next';
import { useGTM } from '@hooks/useGTM';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { isBrandAC, ROLES, isBrandDS } from 'src/constants/main';

const NUMBER_OF_INITIAL_SHOWN_OPTIONS = 3;

export const StockModelCardOptionsTemplate: React.ComponentType<
    StockModelCardOptionsProps &
        TWithCollapsible & {
            childRef?: RefObject<HTMLDivElement>;
        }
> = WithCollapsible(
    ({
        className,
        optionsList,
        withTooltip,
        isCollapsibleOpen,
        childRef,
        openCloseCollapsible,
        bodyStyle,
        index,
        hasOptions,
    }) => {
        const { t } = useTranslations();
        const { pushUserAction } = useGTM();
        const [isMoreOptionPanelOpen, setIsMoreOptionPanelOpen] = useState(false);

        const openMoreOptions = (event: MouseEvent<HTMLLabelElement>) => {
            event.stopPropagation();
            setIsMoreOptionPanelOpen(true);
        };

        const closeMoreOptions = (event: MouseEvent<HTMLLabelElement>) => {
            event.stopPropagation();
            document?.getElementById('__next').removeEventListener('click', closeMoreOptions as any);
            setIsMoreOptionPanelOpen(false);
        };

        useEffect(() => {
            if (isMoreOptionPanelOpen) {
                document?.getElementById('__next').addEventListener('click', closeMoreOptions as any);
            }
        }, [isMoreOptionPanelOpen]);

        if (withTooltip && optionsList.length) {
            const count = optionsList.length;
            return (
                <div className={`${className} stockModelCard-options`}>
                    <div className={`stockModelCard-options--tooltipItem ${hasOptions ? `has-options` : 'no-options'}`}>
                        <span className="cardOptionTooltipItem--tooltipItem__title">
                            <Trans t={t} i18nKey="stockModelCard.tooltipTitle" count={count}>
                                <span>Voir les {{ count }} Options</span>
                            </Trans>
                        </span>
                        <div ref={childRef}>
                            <IconContainer
                                onClick={() => {
                                    openCloseCollapsible();
                                    pushUserAction({
                                        action: 'MoreDetails',
                                        label: bodyStyle.title,
                                    });
                                }}
                            >
                                <AccessibleButton onClick={() => openCloseCollapsible()}>
                                    <IconTemplate
                                        altText={t('banner.button.learnMore')}
                                        altId={`svgImgSeeOptions-${index}`}
                                        className="information"
                                        name={isBrandAC || isBrandDS ? Icons.InfoCircle : Icons.Information}
                                        role={ROLES.IMG}
                                    />
                                </AccessibleButton>
                                <InformationTooltip isVisible={isCollapsibleOpen}>
                                    <TooltipWrapper>
                                        <IconTemplate
                                            className="indicator"
                                            altId={`svgImgSeeOptions-${index}`}
                                            altText={t('stockModelCard.tooltipTitle', { count: count })}
                                            role={ROLES.IMG}
                                            name={Icons.chevronLeft}
                                        />
                                        <ul className="stockModelCard-options--tooltipItemList">
                                            {optionsList.map((item, index) => {
                                                return (
                                                    <li
                                                        key={`${1 + index}`}
                                                        className="stockModelCard-options--tooltipItemListLi"
                                                    >
                                                        {item}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </TooltipWrapper>
                                </InformationTooltip>
                            </IconContainer>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={`${className} stockModelCard-options`}>
                {optionsList.length > 0 ? (
                    <ul className={`stockModelCard-options--list ${hasOptions ? `has-options` : 'no-options'}`}>
                        <h3 className="stockModelCard-options--heading">{t('stockModelCard.options')}</h3>
                        {optionsList.map((item, index) => {
                            if (!item || index >= NUMBER_OF_INITIAL_SHOWN_OPTIONS) {
                                return null;
                            }
                            return (
                                <li key={index} className="stockModelCard-options--item">
                                    {item}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div
                        className={`${hasOptions ? `has-options` : 'no-options'} ${
                            withTooltip ? 'tooltipPlaceholder' : ''
                        }`}
                    />
                )}

                {optionsList.length > NUMBER_OF_INITIAL_SHOWN_OPTIONS && (
                    <label onClick={openMoreOptions} className="stockModel-tooltip">
                        <span className="checkmark">{isMoreOptionPanelOpen ? '-' : '+'}</span>
                        <span>{t('stockModelCard.seeAll')}</span>
                    </label>
                )}

                {isMoreOptionPanelOpen && optionsList.length > NUMBER_OF_INITIAL_SHOWN_OPTIONS && (
                    <ul className="stockModelCard-options--fullList">
                        <h3 className="stockModelCard-options--fullList__heading">{t('stockModelCard.optionsLong')}</h3>

                        {optionsList.map((item, index) => (
                            <li key={index + item} className="stockModelCard-options--fullList__item">
                                {item}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }
);

export default StockModelCardOptionsTemplate;
